import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { OrderAttachment, OrderReferenceType, WorkspacePayload } from './types';

type WorkspaceMode = 'mobile' | 'desktop';
type Subsection = WorkspacePayload['subsections'][number];

interface Props {
  mode: WorkspaceMode;
  workspace: WorkspacePayload;
  busy: boolean;
  onSave: (row: Subsection, values: { title: string; content: string; tableData: string[][]; referenceType: OrderReferenceType }) => Promise<void>;
  onUpload: (row: Subsection, file: File) => Promise<void>;
  onOpenAttachment: (attachment: OrderAttachment) => Promise<void>;
}

const SECTION_LABELS: Record<string, string> = {
  situation: '一、狀況', mission: '二、任務', execution: '三、執行', support: '四、勤務支援', command: '五、指揮與通訊',
};

function parseTableInput(value: string): string[][] {
  return value.split('\n').map((row) => row.split('|').map((cell) => cell.trim())).filter((row) => row.some(Boolean));
}

function storedTableInput(value: string | null): string {
  if (!value) return '';
  try {
    const rows = JSON.parse(value) as unknown;
    return Array.isArray(rows) ? rows.map((row) => Array.isArray(row) ? row.join(' | ') : '').filter(Boolean).join('\n') : '';
  } catch { return ''; }
}

function ReferencePreview({ type, workspace }: { type: OrderReferenceType; workspace: WorkspacePayload }) {
  if (!type) return null;
  if (type === 'timeline') return <div className="order-reference"><strong>勤務時間</strong><ul>{workspace.timeline.filter((row) => row.kind === 'operation').map((row) => <li key={String(row.id)}>{String(row.at)}　{String(row.title)}</li>)}</ul></div>;
  if (type === 'teams') return <div className="order-reference"><strong>執行編組</strong><ul>{workspace.teams.map((team) => <li key={String(team.id)}>{String(team.name)}</li>)}</ul></div>;
  return <div className="order-reference"><strong>車次</strong><ul>{workspace.vehicles.map((vehicle) => <li key={String(vehicle.id)}>{String(vehicle.code)}　{String(vehicle.seat_count)} 人</li>)}</ul></div>;
}

export default function OrderEditor({ mode, workspace, busy, onSave, onUpload, onOpenAttachment }: Props) {
  const [selectedId, setSelectedId] = useState(workspace.subsections[0]?.id ?? '');
  const selected = workspace.subsections.find((row) => row.id === selectedId) ?? workspace.subsections[0];
  const [title, setTitle] = useState(selected?.title ?? '');
  const [content, setContent] = useState(selected?.content ?? '');
  const [tableInput, setTableInput] = useState(storedTableInput(selected?.table_data ?? null));
  const [referenceType, setReferenceType] = useState<OrderReferenceType>(selected?.reference_type ?? '');
  const [file, setFile] = useState<File | null>(null);
  const table = useMemo(() => parseTableInput(tableInput), [tableInput]);
  const attachments = selected ? workspace.orderAttachments.filter((item) => item.subsectionId === selected.id) : [];

  useEffect(() => {
    if (!selected) return;
    setTitle(selected.title);
    setContent(selected.content ?? '');
    setTableInput(storedTableInput(selected.table_data));
    setReferenceType(selected.reference_type ?? '');
    setFile(null);
  }, [selected?.id, selected?.row_version]);

  if (!selected) return <p className="empty-state">尚未建立命令小標。</p>;

  const navigation = <nav className="order-outline" aria-label="命令段落">
    {Object.entries(SECTION_LABELS).map(([type, label]) => <div key={type}>
      <strong>{label}</strong>
      {workspace.subsections.filter((row) => row.section_type === type).map((row) => <button
        type="button" key={row.id} aria-current={row.id === selected.id ? 'true' : undefined}
        onClick={() => setSelectedId(row.id)}>{row.title}</button>)}
    </div>)}
  </nav>;

  const editor = <form className="order-editor-form" onSubmit={(event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSave(selected, { title, content, tableData: table, referenceType });
  }}>
    <p className="section-kicker">{SECTION_LABELS[selected.section_type]}</p>
    <label>小標<input value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={120} /></label>
    <label>內容<textarea value={content} onChange={(event) => setContent(event.target.value)} rows={mode === 'desktop' ? 12 : 7} /></label>
    <details className="structured-tools">
      <summary>加入表格或案件資料</summary>
      <label>簡易表格<textarea value={tableInput} onChange={(event) => setTableInput(event.target.value)} rows={4} placeholder={'時間 | 工作 | 負責人\n08:30 | 場地確認 | 第一組'} /></label>
      <small>每行一列，用「|」分欄；最多 20 列、每列 8 欄。</small>
      <label>引用案件資料<select value={referenceType} onChange={(event) => setReferenceType(event.target.value as OrderReferenceType)}>
        <option value="">不引用</option><option value="timeline">勤務時間</option><option value="teams">執行編組</option><option value="vehicles">車次</option>
      </select></label>
    </details>
    <button className="primary-button" disabled={busy}>儲存這一段</button>
  </form>;

  const attachmentPanel = <div className="order-attachments">
    <h4>圖片與 PDF</h4>
    {attachments.map((item) => <button className="text-button" type="button" key={item.id} onClick={() => void onOpenAttachment(item)}>{item.fileName}</button>)}
    <label className="file-picker">選擇附件<input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
    <button className="secondary-button compact" type="button" disabled={busy || !file} onClick={() => file && void onUpload(selected, file)}>上傳附件</button>
  </div>;

  const preview = <article className="order-live-preview" aria-label="即時預覽">
    <p className="section-kicker">即時預覽</p><h3>{title || '未命名小標'}</h3>
    <p className="preserve-lines">{content || '尚未填寫內容。'}</p>
    {table.length > 0 && <div className="table-scroll"><table><tbody>{table.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>}
    <ReferencePreview type={referenceType} workspace={workspace} />
    {attachments.length > 0 && <div><strong>附件</strong><ul>{attachments.map((item) => <li key={item.id}>{item.fileName}</li>)}</ul></div>}
  </article>;

  if (mode === 'mobile') return <div className="mobile-order-editor">
    <label className="mobile-subsection-select">選擇要快修的小標<select value={selected.id} onChange={(event) => setSelectedId(event.target.value)}>{workspace.subsections.map((row) => <option value={row.id} key={row.id}>{SECTION_LABELS[row.section_type]}｜{row.title}</option>)}</select></label>
    {editor}{attachmentPanel}
  </div>;

  return <div className="desktop-order-editor">{navigation}<div className="order-editor-column">{editor}{attachmentPanel}</div>{preview}</div>;
}
